import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot'
import { faMicroscope } from '@fortawesome/pro-regular-svg-icons/faMicroscope'
import { faPollPeople } from '@fortawesome/pro-regular-svg-icons/faPollPeople'
import { faPrescriptionBottleMedical } from '@fortawesome/pro-regular-svg-icons/faPrescriptionBottleMedical'
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles'
import { getPreferredTrialTitleForAlgoliaTrial } from '@modules/trials/helpers/getPreferredTrialTitle'
import { STATUS_LABELS } from '@modules/trials/search/const'
import {
  isAgeRangeGeneric,
  transformAgeRangeFromAlgolia,
} from '@modules/trials/search/helpers/transformAgeRangeFromAlgolia'
import some from 'lodash/some'
import { CallToAction } from './CallToAction.client'
import { DetailPill } from './DetailPill'
import { HighlightTag } from './HighlightTag'
import { KeyEligibilityCriteria } from './KeyEligibilityCriteria'
import { SiteStatusBadge } from './SiteStatusBadge'
import { TrialTitleWithConditionHighlight } from './TrialTitleWithConditionHighlight'
import type { SearchTrialCardProps } from './types'

export default function TrialCard({
  abbreviatedTitle,
  activeContractWithSponsor,
  biologicalSex,
  briefSummary,
  condensedPhaseList,
  conjuredTitle,
  currentRank,
  enrollmentCount,
  hasBackgroundTherapyAllowed,
  hasNoPlacebo,
  isBreakthroughTrial,
  locationCities,
  locationStates,
  maximumAge,
  medicationsDisqualifyingParticipation,
  medicationsRequiredForParticipation,
  minimumAge,
  nctId,
  nlpBriefSummary,
  overallStatus,
  participationDisqualifiers,
  phaseList,
  primaryCondition,
  slug,
  treatments,
}: SearchTrialCardProps) {
  const hasRankOrActiveContract = activeContractWithSponsor ?? currentRank
  const preferredTitle = getPreferredTrialTitleForAlgoliaTrial(
    {
      abbreviatedTitle: abbreviatedTitle ?? null,
      conjuredTitle: conjuredTitle ?? null,
      nctId: nctId ?? '',
      treatments: treatments ?? [],
    },
    primaryCondition ?? '',
  )

  const preferredDescription = nlpBriefSummary ?? briefSummary ?? ''
  const isAdjuvantTrial = hasBackgroundTherapyAllowed
  const isPivotalTrial = phaseList?.includes('Phase 3')

  const hasHighlights = some([
    isAdjuvantTrial,
    isBreakthroughTrial,
    isPivotalTrial,
    hasNoPlacebo,
  ])

  const phaseListLabel = condensedPhaseList

  const displayLocation = [locationCities?.[0], locationStates?.[0]]
    .filter(Boolean)
    .join(', ')

  const ageRange = transformAgeRangeFromAlgolia(minimumAge, maximumAge)

  return (
    <div
      className={classMerge(
        'relative mx-4 max-w-[905px] overflow-hidden rounded-2xl border-[1.5px] border-neutral200 bg-white shadow-sm transition-shadow hover:shadow-md lg:mx-0 lg:min-h-[239px]',
        activeContractWithSponsor && 'border-primary100',
        currentRank === 'PowerPreferred' && 'border-primary100',
        currentRank === 'SuperSite' && 'border-primary200',
      )}
    >
      <SiteStatusBadge
        activeContractWithSponsor={activeContractWithSponsor}
        currentRank={currentRank}
      />
      <div className='grid auto-rows-auto gap-2 p-6 lg:gap-4'>
        <div className='grid min-h-24 grid-cols-1 gap-4 lg:grid-cols-[286px_1fr] lg:gap-6'>
          <div className='flex'>
            <div className='flex w-full flex-col justify-start'>
              <TrialTitleWithConditionHighlight
                slug={slug}
                trialTitle={preferredTitle}
              />
              <div className='mt-2 flex items-center gap-1'>
                <Icon className='text-primary600' icon={faLocationDot} />
                <span className='whitespace-nowrap text-xs font-semibold leading-normal text-black antialiased'>
                  {displayLocation}
                </span>
              </div>
            </div>
          </div>
          <div>
            <span
              className={classMerge(
                'line-clamp-2 select-none text-[length:0.8125rem] font-medium leading-[160%] text-neutral900 antialiased lg:line-clamp-3',
                !hasHighlights && 'lg:line-clamp-4',
              )}
              title={preferredDescription}
            >
              {preferredDescription}
            </span>
            <div className='mt-2 grid grid-flow-col grid-rows-2 gap-y-1 lg:flex lg:gap-x-4'>
              {isAdjuvantTrial && (
                <HighlightTag
                  icon={faPrescriptionBottleMedical}
                  value='Stay on current meds'
                />
              )}
              {hasNoPlacebo && (
                <HighlightTag icon={faPollPeople} value='No Placebo Group' />
              )}
              {isBreakthroughTrial && (
                <HighlightTag icon={faSparkles} value='Prior Safety Data' />
              )}
              {isPivotalTrial && (
                <HighlightTag
                  icon={faMicroscope}
                  value='Pivotal Trial (Near Approval)'
                />
              )}
            </div>
          </div>
        </div>
        {/* Row 2: Trial details and highlights */}
        <div className='grid grid-cols-1 gap-2 lg:grid-cols-[286px_1fr] lg:gap-4'>
          <div>
            <p className='text-xs font-semibold leading-normal text-neutral500 antialiased'>
              Trial Details
            </p>
            <div className='mt-2 flex flex-wrap gap-2'>
              <DetailPill
                bgColor='bg-neutral100'
                prefix='Trial Status'
                value={
                  STATUS_LABELS[overallStatus as keyof typeof STATUS_LABELS] ||
                  overallStatus
                }
              />
              <DetailPill
                bgColor='bg-neutral100'
                prefix='Trial Phase'
                value={phaseListLabel}
              />
              {!isAgeRangeGeneric({
                maximumAge,
                minimumAge,
              }) && (
                <DetailPill
                  bgColor='bg-neutral100'
                  prefix='Age'
                  value={ageRange}
                />
              )}
              {biologicalSex !== 'All' && (
                <DetailPill
                  bgColor='bg-neutral100'
                  prefix='Sex'
                  value={biologicalSex}
                />
              )}
            </div>
          </div>
          <div className='flex justify-end'>
            <KeyEligibilityCriteria
              medicationsDisqualifyingParticipation={
                medicationsDisqualifyingParticipation
              }
              medicationsRequiredForParticipation={
                medicationsRequiredForParticipation
              }
              participationDisqualifiers={participationDisqualifiers}
            />

            <div className='w-full lg:w-auto'>
              <CallToAction
                enrollmentCount={enrollmentCount}
                gradient={!!hasRankOrActiveContract}
                slug={slug}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
